/**
 * Radio Component
 * Based on shadcn/ui + Figma design specification
 *
 * @description shadcn/ui 기반 컴포넌트. CSS 변수를 활용한 테마 시스템 적용.
 *
 * States: Default, Focus, Error, Error Focus, Disabled
 * Checked States: False, True
 */

import React, { forwardRef, ReactNode, InputHTMLAttributes } from 'react';
import { cn } from '../lib/utils';

export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** 체크 상태 */
  checked?: boolean;
  /** 에러 상태 */
  error?: boolean;
  /** 라벨 */
  label?: string;
  /** 추가 클래스 */
  className?: string;
}

export interface RadioGroupProps {
  /** 라디오 그룹 레이아웃 */
  layout?: 'inline' | 'block';
  /** 자식 요소 */
  children: ReactNode;
  /** 추가 클래스 */
  className?: string;
}

export interface RichRadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** 체크 상태 */
  checked?: boolean;
  /** 라벨 */
  label: string;
  /** 보조 텍스트 */
  secondaryText?: string;
  /** 라디오 위치 반전 (오른쪽) */
  flipped?: boolean;
  /** 추가 클래스 */
  className?: string;
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      checked,
      error = false,
      disabled,
      label,
      className,
      onChange,
      ...props
    },
    ref
  ) => {
    const getRadioStyles = () => {
      const base = 'w-4 h-4 rounded-full border-2 transition-all duration-200 appearance-none cursor-pointer';

      if (disabled) {
        return `${base} opacity-50 cursor-not-allowed ${checked ? 'bg-primary border-primary' : 'border-border bg-white'}`;
      }

      if (checked) {
        if (error) {
          return `${base} bg-danger border-danger focus:ring-2 focus:ring-danger/20`;
        }
        return `${base} bg-primary border-primary focus:ring-2 focus:ring-primary/20`;
      }

      if (error) {
        return `${base} border-danger bg-white focus:ring-2 focus:ring-danger/20`;
      }

      return `${base} border-border bg-white hover:border-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20`;
    };

    const radioElement = (
      <div className={cn('relative inline-flex items-center justify-center', className)}>
        <input
          ref={ref}
          type="radio"
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          className={getRadioStyles()}
          {...props}
        />
        {checked && (
          <span className="absolute pointer-events-none w-1.5 h-1.5 rounded-full bg-white" />
        )}
      </div>
    );

    if (label) {
      return (
        <label className={cn('inline-flex items-center gap-2 cursor-pointer', disabled && 'opacity-50 cursor-not-allowed')}>
          {radioElement}
          <span className="text-sm text-foreground">{label}</span>
        </label>
      );
    }

    return radioElement;
  }
);

Radio.displayName = 'Radio';

// Radio Group Component
export const RadioGroup: React.FC<RadioGroupProps> = ({
  layout = 'block',
  children,
  className,
}) => {
  return (
    <div
      role="radiogroup"
      className={cn(
        layout === 'inline' ? 'flex flex-wrap items-center gap-4' : 'flex flex-col gap-2',
        className
      )}
    >
      {children}
    </div>
  );
};

RadioGroup.displayName = 'RadioGroup';

// Rich Radio Component
export const RichRadio = forwardRef<HTMLInputElement, RichRadioProps>(
  (
    {
      checked,
      label,
      secondaryText,
      flipped = false,
      disabled,
      className,
      onChange,
      ...props
    },
    ref
  ) => {
    const radioElement = (
      <div className="relative inline-flex items-center justify-center flex-shrink-0">
        <input
          ref={ref}
          type="radio"
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          className={cn(
            'w-4 h-4 rounded-full border-2 transition-all duration-200 appearance-none cursor-pointer',
            checked
              ? 'bg-primary border-primary'
              : 'border-border bg-white hover:border-muted-foreground',
            disabled && 'opacity-50 cursor-not-allowed',
            'focus:ring-2 focus:ring-primary/20'
          )}
          {...props}
        />
        {checked && (
          <span className="absolute pointer-events-none w-1.5 h-1.5 rounded-full bg-white" />
        )}
      </div>
    );

    const labelContent = (
      <div className="flex flex-col">
        <span className="text-sm font-medium text-foreground">{label}</span>
        {secondaryText && (
          <span className="text-xs text-muted-foreground">{secondaryText}</span>
        )}
      </div>
    );

    return (
      <label
        className={cn(
          'inline-flex items-center gap-3 p-3 rounded-lg border border-accent-2 bg-accent-0',
          'cursor-pointer transition-all duration-200',
          'hover:border-border',
          checked && 'border-primary bg-primary/5',
          disabled && 'opacity-50 cursor-not-allowed',
          flipped && 'flex-row-reverse justify-between',
          className
        )}
      >
        {radioElement}
        {labelContent}
      </label>
    );
  }
);

RichRadio.displayName = 'RichRadio';
